/**
 * Navigation surfaces and the permission each one requires.
 *
 * One table, two consumers: the sidebar hides entries the user has no
 * permission for, and the route guard blocks the same paths when typed
 * directly. Keeping them on a single source means a destination cannot be
 * hidden from the nav while staying reachable by URL.
 *
 * SCOPE, stated plainly: this gates the destinations declared below and their
 * aliases. Paths outside this table (settings, mobile shells, deep links) are
 * NOT gated here — they rely on the API refusing the request, which is the real
 * enforcement point. This table is about not showing someone a door that will
 * 403, not about being the lock.
 */
import {
  Activity,
  AlertCircle,
  CalendarDays,
  ClipboardList,
  Grid3X3,
  Inbox,
  Landmark,
  LayoutDashboard,
  Megaphone,
  Shield,
} from 'lucide-react';
import { hasPermission, PERMISSIONS, type PermissionValue } from '@/lib/permissions';
import { MARKETING_UI_ENABLED } from '@/lib/featureFlags';

/**
 * Feature switches that can remove a destination for everyone, regardless of
 * permission. Distinct from permissions: a permission says who may open a
 * door, a feature flag says whether the door exists right now.
 */
export type NavFeature = 'marketing';

export interface NavFeatureFlags {
  marketingUiEnabled: boolean;
}

export interface NavItem {
  name: string;
  path: string;
  icon: typeof LayoutDashboard;
  aliases?: string[];
  /**
   * Permission required to see and open this entry. REQUIRED, not optional —
   * an entry with no declared permission would be hidden from everyone rather
   * than shown to everyone, so adding a destination without thinking about
   * access fails closed and visibly.
   */
  requiredPermission: PermissionValue;
  /** When set, the entry is dropped entirely while that feature is off. */
  feature?: NavFeature;
  /**
   * When set, the entry stays but takes this name while that feature is off.
   * Used for a destination that keeps one live surface through a dormancy.
   */
  whileDormant?: { feature: NavFeature; name: string };
}

/**
 * Every destination the app knows about, including ones a feature flag may be
 * hiding. Consumers should use NAV_ITEMS, which has the flags applied.
 */
export const ALL_NAV_ITEMS: NavItem[] = [
  {
    name: 'Today',
    path: '/todays-calls',
    icon: LayoutDashboard,
    requiredPermission: PERMISSIONS.DASHBOARD_VIEW,
  },
  { name: 'Rates', path: '/command', icon: Grid3X3, requiredPermission: PERMISSIONS.RATES_VIEW },
  {
    name: 'Work Queue',
    path: '/workspace/queue',
    icon: ClipboardList,
    aliases: [
      '/marketing-approvals',
      '/engagement-review',
      '/approvals',
      '/content-review',
      '/organic-content',
      '/publishing-ops',
      '/organic-packages',
    ],
    // Approval surface. Marketing partners review nothing.
    requiredPermission: PERMISSIONS.RATES_VIEW,
  },
  {
    name: 'Calendar',
    path: '/events',
    icon: CalendarDays,
    requiredPermission: PERMISSIONS.EVENTS_VIEW,
  },
  {
    name: 'Intelligence',
    path: '/intelligence',
    icon: Activity,
    aliases: [
      '/kpis',
      '/insights',
      '/forecasting',
      '/acquisitions',
      '/labor',
      '/results',
      '/site-analytics',
    ],
    // Carries the competitive set, KPIs and forecasting.
    requiredPermission: PERMISSIONS.COMPETITORS_VIEW,
  },
  {
    name: 'Marketing',
    path: '/marketing-command-center',
    icon: Megaphone,
    aliases: ['/marketing-analytics'],
    requiredPermission: PERMISSIONS.MARKETING_VIEW,
    // Stays reachable while marketing is dormant: the command center's Web
    // Analytics tab is the first-party measurement surface for the agency's
    // ad test, and a marketing partner has no other door.
    whileDormant: { feature: 'marketing', name: 'Web Analytics' },
  },
  {
    name: 'Brand Defence',
    path: '/brand-defence',
    icon: Shield,
    requiredPermission: PERMISSIONS.MARKETING_VIEW,
    feature: 'marketing',
  },
  {
    name: 'Finance',
    path: '/finance',
    icon: Landmark,
    aliases: ['/finance/deals'],
    requiredPermission: PERMISSIONS.FINANCE_VIEW,
  },
  {
    name: 'Leads',
    path: '/concierge-leads',
    icon: Inbox,
    requiredPermission: PERMISSIONS.DASHBOARD_VIEW,
  },
  {
    name: 'System',
    path: '/system',
    icon: AlertCircle,
    aliases: ['/control-tower', '/system-map', '/learning', '/oracle-performance'],
    // AUDIT_VIEW rather than SYSTEM_ADMIN: the live property_admin accounts
    // need System, and none of them are super_admin.
    requiredPermission: PERMISSIONS.AUDIT_VIEW,
  },
];

function featureEnabled(feature: NavFeature | undefined, flags: NavFeatureFlags): boolean {
  if (!feature) return true;
  switch (feature) {
    case 'marketing':
      return flags.marketingUiEnabled;
  }
}

/** The nav table with feature flags applied. Pure, so tests can flip flags. */
export function navItemsForFlags(flags: NavFeatureFlags): NavItem[] {
  return ALL_NAV_ITEMS.filter((item) => featureEnabled(item.feature, flags)).map((item) =>
    item.whileDormant && !featureEnabled(item.whileDormant.feature, flags)
      ? { ...item, name: item.whileDormant.name }
      : item,
  );
}

export const NAV_FEATURE_FLAGS: NavFeatureFlags = { marketingUiEnabled: MARKETING_UI_ENABLED };

export const NAV_ITEMS: NavItem[] = navItemsForFlags(NAV_FEATURE_FLAGS);

/**
 * Routes that render a full marketing page and have nothing to show while
 * marketing is dormant; they render the dormant notice instead. The command
 * center is NOT here: it stays live for its Web Analytics tab. The Work Queue
 * aliases keep redirecting into the queue, which hides its own marketing tabs.
 */
export const MARKETING_PAGE_ROUTES: string[] = ALL_NAV_ITEMS.filter(
  (item) => item.feature === 'marketing',
).flatMap((item) => [item.path, ...(item.aliases ?? [])]);

/**
 * Where to send someone on sign-in: their usual landing if they can open it,
 * otherwise the first nav destination they hold a permission for.
 *
 * Returns the usual landing when nothing matches. That path is guarded anyway,
 * so a user with no permissions at all sees the honest access message rather
 * than being redirected in a loop.
 */
export function resolveDefaultLanding(
  user: { permissions: string[] } | null | undefined,
  decisionLandingEnabled: boolean,
  items: NavItem[] = NAV_ITEMS,
): string {
  const preferred = decisionLandingEnabled ? '/todays-calls' : '/command';
  if (!user) return preferred;

  const preferredPermission = requiredPermissionForPath(preferred, items);
  if (!preferredPermission || hasPermission(user, preferredPermission)) return preferred;

  const firstAllowed = items.find((item) => hasPermission(user, item.requiredPermission));
  return firstAllowed?.path ?? preferred;
}

function matchesPath(pathname: string, candidate: string): boolean {
  return pathname === candidate || pathname.startsWith(`${candidate}/`);
}

/**
 * The permission a pathname requires, or null when the path is not one of the
 * declared nav surfaces.
 *
 * Longest match wins, so a nested path under two entries resolves to the more
 * specific one rather than whichever happens to be declared first.
 */
export function requiredPermissionForPath(
  pathname: string,
  items: NavItem[] = NAV_ITEMS,
): PermissionValue | null {
  let best: { length: number; permission: PermissionValue } | null = null;

  for (const item of items) {
    for (const candidate of [item.path, ...(item.aliases ?? [])]) {
      if (!matchesPath(pathname, candidate)) continue;
      if (!best || candidate.length > best.length) {
        best = { length: candidate.length, permission: item.requiredPermission };
      }
    }
  }

  return best?.permission ?? null;
}
